import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useContent } from '../../contexts/ContentContext';

const LOGO_URL = "/images/pool-service-logo-white.png";
const API = process.env.REACT_APP_BACKEND_URL;

const defaultNavLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services', children: [
    { name: 'Residential Pool Cleaning', path: '/services/residential' },
    { name: 'Pool Repair & Maintenance', path: '/services/repair-maintenance' },
    { name: 'Pool Renovations', path: '/services/renovations' },
    { name: 'Commercial Services', path: '/services/commercial' },
  ]},
  { name: 'About Us', path: '/about' },
  { name: 'Contact Us', path: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [navLinks, setNavLinks] = useState(defaultNavLinks);
  const location = useLocation();
  const { global } = useContent();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  // Fetch dynamic navigation
  useEffect(() => {
    fetch(`${API}/api/navigation`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.items?.length) setNavLinks(data.items);
      })
      .catch(() => {}); // Use defaults on error
  }, []);

  const isHomePage = location.pathname === '/';

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHomePage
            ? 'bg-[#0F172A] shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className={`flex items-center justify-between transition-all duration-300 ${
            isScrolled ? 'h-16' : 'h-24 sm:h-28'
          }`}>
            {/* Logo with scroll animation */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <motion.img
                src={LOGO_URL}
                alt="A-Quality Pool Service"
                width={114}
                height={80}
                className="w-auto transition-all duration-300"
                style={{
                  height: isScrolled ? '50px' : (isHomePage ? '80px' : '60px')
                }}
                initial={false}
                animate={{
                  height: isScrolled ? 50 : (isHomePage ? 80 : 60)
                }}
                transition={{ duration: 0.3 }}
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navLinks.map((link) => (
                link.children && link.children.length > 0 ? (
                  <DropdownMenu key={link.name}>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`flex items-center text-white hover:text-sky-400 transition-colors font-medium ${
                          location.pathname === link.path ? 'text-sky-400' : ''
                        }`}
                      >
                        {link.name}
                        <ChevronDown className="ml-1 h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-white border-slate-200">
                      {link.children.map((item) => (
                        <DropdownMenuItem key={item.name} asChild>
                          <Link
                            to={item.path}
                            data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className="cursor-pointer"
                          >
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`nav-link text-white hover:text-sky-400 transition-colors font-medium ${
                      location.pathname === link.path ? 'text-sky-400' : ''
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {/* CTA Button & Phone */}
            <div className="hidden lg:flex items-center space-x-6">
              <a
                href={`tel:${global.phone}`}
                data-testid="phone-link"
                className="flex items-center text-white hover:text-sky-400 transition-colors"
              >
                <Phone className="h-5 w-5 mr-2" />
                <span className="font-semibold">{global.phone}</span>
              </a>
              <Button
                asChild
                data-testid="get-quote-btn"
                className="bg-sky-500 hover:bg-sky-400 text-white rounded-full px-6"
              >
                <Link to="/contact">Get Free Quote</Link>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div className="absolute inset-0 bg-[#0F172A]" />
            <div className="relative h-full flex flex-col pt-24 px-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {link.children && link.children.length > 0 ? (
                    <div className="py-4 border-b border-slate-700">
                      <Link
                        to={link.path}
                        className="text-2xl font-semibold text-white hover:text-sky-400 transition-colors"
                      >
                        {link.name}
                      </Link>
                      <div className="mt-2 ml-4 space-y-2">
                        {link.children.map((item) => (
                          <Link
                            key={item.name}
                            to={item.path}
                            className="block text-slate-300 hover:text-sky-400 transition-colors"
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={link.path}
                      className="block py-4 text-2xl font-semibold text-white hover:text-sky-400 transition-colors border-b border-slate-700"
                    >
                      {link.name}
                    </Link>
                  )}
                </motion.div>
              ))}
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 space-y-4"
              >
                <a
                  href={`tel:${global.phone}`}
                  className="flex items-center justify-center text-xl text-white"
                >
                  <Phone className="h-6 w-6 mr-3" />
                  {global.phone}
                </a>
                <Button
                  asChild
                  className="w-full bg-sky-500 hover:bg-sky-400 text-white rounded-full py-6 text-lg"
                >
                  <Link to="/contact">Get Free Quote</Link>
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
